import { arc, closing, ring, rush, size, stream, wall } from '../sim/formations';
import { spawnEliteSquad } from '../sim/elites';
import { idByTint } from './enemies';
/* ============================================================
   波次劇本 —— 一波不是「一團敵人」，是一段有名字的作戰
   ============================================================

   M12 之前：每 45 秒從五種編隊裡抽一種，一次擺完就結束。
   問題不在數量而在**讀不出意圖** —— 玩家看到的永遠是「又來一批」，
   所以密度再高也只是背景噪音（roadmap 0.3 診斷 4）。

   這裡把一波拆成**梯次**（echelon），每個梯次有自己的入場時間、
   方向、速度與敵種，而且共用同一個「來襲方位」`c.a`：

     前鋒 scout   少量、快、先到 —— 告訴玩家「有事要發生，在那個方向」
     主體 main    這一波的形狀本體（牆／環／衝鋒）
     收尾 anchor  主體之後 1.5–3 秒才到，通常是硬目標或遠程

   三段式的重點是**節奏**：玩家先看到前鋒、處理掉、以為過去了，
   主體才成形。一次擺完的話這三拍全部壓成一拍。

   每一波都有名字與一句無線電旁白。那不是裝飾 ——
   支柱一要求「每一種會致死的東西都要先講一次」，而「講」得讓人看得懂：
   方位（由哪邊來）＋ 意圖（要幹嘛）＋ 一秒的預告時間。

   新增一波的判準與池子一樣：**它改變了玩家接下來十秒的動作嗎？**
   只是「數量更多的牆」就不要加。 */

/** 這個時間點該用哪一級的雜兵。**一律經過 `idByTint`**，所以劇本永遠
    挑不到「還沒被介紹過」的敵種 —— 支柱一要求每一種會致死的東西都要
    先講一次，而波次是最不適合初次見面的場合。

    四個角色對到四個威脅色，這是刻意的：劇本說「這一波要一堆直射的」
    比說「這一波要一堆 LANZE」更耐改 —— 之後 SPULE、KRANZ 移植進來，
    同一個劇本自動就會用到它們。

    ⚠️ 只有一個家族時 `light`/`fast` 會挑到同一隻（都是紅）。
    這是暫時的：WEDGE 補進來之後 `fast` 就會拿到 KEIL。 */
function light(t) { return idByTint(t, 'red'); }
function fast(t) { return idByTint(t, 'red'); }
function heavy(t) { return idByTint(t, 'vio'); }
function gun(t) { return idByTint(t, 'amb'); }

export var WAVES = [
  /* 巡邏編隊：溪流當前鋒（一條線，看得出方向），三秒後同方向來一整面牆。
     最早的一種，所以最單純：**它教玩家「預告的方位是真的」**。 */
  {
    id: 'patrol', n: '巡邏編隊', from: 60,
    radio: function (c) { return '偵測到巡邏編隊改變航向 · ' + c.dir + '方接近'; },
    ech: [
      { at: 0, run: function (c) { return stream({ a: c.a, dur: 3.0, kind: light(c.t), gap: 0.24 }); } },
      { at: 3.0, run: function (c) { return wall({ a: c.a, kind: light(c.t) }); } }
    ]
  },
  /* 鉗形攻勢：兩道牆對向同時進場，2.6 秒後一圈突刺者收口。
     解法是「往牆的側面走」—— 這是第一種**不能靠往預告的反方向跑**解掉的波。 */
  {
    id: 'pincer', n: '鉗形攻勢', from: 240,
    radio: function (c) { return '敵軍分兵包抄 · ' + c.dir + '方與其對側同時進場'; },
    ech: [
      { at: 0, run: function (c) { return wall({ a: c.a, kind: light(c.t) }); } },
      { at: 0.15, run: function (c) { return wall({ a: c.a + Math.PI, kind: light(c.t) }); } },
      { at: 2.6, run: function (c) { return ring({ n: Math.round(size(9)), dist: 24, kind: fast(c.t) }); } }
    ]
  },
  /* 封鎖線：收縮環 + 三秒後外圈架起遠程。
     突圍是對的解，但**突圍出去會撞到火力** —— 逼玩家選一個方向而不是隨便跑。 */
  {
    id: 'siege', n: '封鎖線', from: 330,
    radio: function (c) { return '封鎖線收攏 · 外圍已於' + c.dir + '方佈署火力'; },
    ech: [
      { at: 0, run: function (c) { return closing({ n: Math.round(size(20)), kind: heavy(c.t) }); } },
      { at: 3.0, run: function (c) { return arc({ a: c.a, n: 6, dist: 31, kind: gun(c.t) }); } }
    ]
  },
  /* 獵殺小隊：精英成組 + 1.5 秒後護衛環。
     這一波把「精英」從隨機出現的路障變成一個**事件** ——
     它有預告、有名字、有隨行部隊，而且掉的寶箱是玩家追著它跑的理由。 */
  {
    id: 'hunt', n: '獵殺小隊', from: 400,
    radio: function (c) { return '敵方獵殺小隊已鎖定你的訊號 · 來自' + c.dir + '方'; },
    ech: [
      { at: 0, run: function (c) { return spawnEliteSquad(c.t, c.a, true); } },
      { at: 1.5, run: function (c) { return ring({ n: Math.round(size(18)), dist: 27, kind: heavy(c.t) }); } }
    ]
  },
  /* 閃擊：三次衝鋒，每次換一個方向。
     **罰「躲完就放鬆」** —— 第一道穿過去之後玩家通常會停下來輸出，
     第二道就是從那個時候開始接近的。 */
  {
    id: 'blitz', n: '連續突擊', from: 520,
    radio: function (c) { return '高速接觸 · 連續三次突擊，首波自' + c.dir + '方'; },
    ech: [
      { at: 0, run: function (c) { return rush({ a: c.a, kind: fast(c.t) }); } },
      { at: 2.2, run: function (c) { return rush({ a: c.a + 2.0, kind: fast(c.t) }); } },
      { at: 4.4, run: function (c) { return rush({ a: c.a - 2.0, kind: fast(c.t) }); } }
    ]
  },
  /* 蟲潮：三層包圍環，一層比一層近、一層比一層厚。
     密度的尖峰在這裡 —— 它存在的理由是讓「同屏 200 隻」變成一個**看得出來的**時刻，
     而不是整場都均勻地滿。 */
  {
    id: 'swarm', n: '蟲潮', from: 600,
    radio: function () { return '大量熱源自地層湧出 · 密度持續上升'; },
    ech: [
      { at: 0, run: function (c) { return ring({ n: Math.round(size(18)), dist: 31, kind: light(c.t) }); } },
      { at: 2.4, run: function (c) { return ring({ n: Math.round(size(18)), dist: 26, kind: light(c.t) }); } },
      { at: 4.8, run: function (c) { return ring({ n: Math.round(size(22)), dist: 21, kind: fast(c.t) }); } }
    ]
  },
  /* 火力壓制：兩側架遠程，中間推一道牆。
     這是唯一「站著不動就會死」的波 —— 遠程不追你，但它們封住的是**你想站的地方**。 */
  {
    id: 'barrage', n: '火力壓制', from: 780,
    radio: function (c) { return '壓制火力就位 · ' + c.dir + '方兩翼交叉射界'; },
    ech: [
      { at: 0, run: function (c) { return arc({ a: c.a, n: 6, dist: 32, kind: gun(c.t) }); } },
      { at: 1.4, run: function (c) { return arc({ a: c.a + Math.PI, n: 6, dist: 32, kind: gun(c.t) }); } },
      { at: 3.0, run: function (c) { return wall({ a: c.a + 1.57, kind: heavy(c.t), n: Math.round(size(13)) }); } }
    ]
  }
];

export function waveById(id) {
  for (var i = 0; i < WAVES.length; i++) if (WAVES[i].id === id) return WAVES[i];
  return null;
}
